import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ClipboardList, Settings, Loader2, UserCheck, Phone, MessageCircle, ShoppingBag, Monitor, UtensilsCrossed } from "lucide-react";
import POSHeader from "./POSHeader";

export default function ContasView({ authAxios, onBack, onSelectTable, onClienteOrder }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("mesas");
  const [tableCode, setTableCode] = useState("");

  useEffect(() => {
    const fetchTables = () => {
      authAxiosRef.current.get("/pos/tables-overview")
        .then(r => setTables(r.data))
        .catch(() => authAxiosRef.current.get("/tables").then(r => setTables(r.data)).catch(() => {}))
        .finally(() => setLoading(false));
    };
    fetchTables();
    const interval = setInterval(fetchTables, 8000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = [
    { id: "codigo", label: "CODIGO" },
    { id: "abertas", label: "ABERTAS" },
    { id: "mesas", label: "MESAS" },
    { id: "cliente", label: "CLIENTE" },
  ];

  const filtered = tab === "abertas"
    ? tables.filter(t => t.status === "occupied" || t.has_open_order)
    : tables;

  const handleCodeSearch = () => {
    const code = tableCode.trim();
    if (!code) return;
    // Match by number or name
    const found = tables.find(t =>
      String(t.number) === code ||
      t.name?.toLowerCase() === code.toLowerCase()
    );
    if (found) {
      onSelectTable({ ...found, isOccupied: found.status === "occupied" || found.has_open_order });
    } else {
      toast.error(`Mesa "${code}" não encontrada`);
    }
  };

  const resetStuckTables = async () => {
    try {
      const res = await authAxios.post("/tables/reset-stuck");
      const data = res.data?.data || res.data;
      toast.success(data.message || "Mesas resetadas");
      authAxios.get("/pos/tables-overview")
        .then(r => setTables(r.data))
        .catch(() => authAxios.get("/tables").then(r => setTables(r.data)).catch(() => {}));
    } catch { toast.error("Erro ao resetar mesas"); }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex flex-col" data-testid="pos-contas">
      <POSHeader title="Contas" icon={ClipboardList} onBack={onBack}
        rightContent={
          <div className="flex gap-2">
            <button onClick={resetStuckTables} className="h-8 px-3 rounded-lg bg-white/20 flex items-center justify-center text-[10px] font-bold tracking-wide" data-testid="reset-stuck-btn" title="Liberar mesas travadas">RESET</button>
            <button className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center"><Settings className="w-4 h-4" /></button>
          </div>
        }
      />
      {/* Tabs */}
      <div className="bg-white border-b flex">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 py-3 text-xs font-bold tracking-wide transition-colors ${
              tab === t.id
                ? t.id === "cliente" ? "text-purple-600 border-b-2 border-purple-600" : "text-emerald-600 border-b-2 border-emerald-600"
                : "text-gray-400"
            }`}
            data-testid={`contas-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "codigo" ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-xs space-y-4">
            <p className="text-center text-sm text-gray-500 font-medium">Digita o número ou nome da mesa</p>
            <Input
              placeholder="Ex: 1, 2, MESA 03..."
              className="h-14 text-center text-2xl font-black"
              value={tableCode}
              onChange={e => setTableCode(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCodeSearch()}
              autoFocus
              data-testid="codigo-input"
            />
            <Button
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 font-bold"
              onClick={handleCodeSearch}
              disabled={!tableCode.trim()}
            >
              Buscar Mesa
            </Button>
            {/* Quick number pad */}
            <div className="grid grid-cols-3 gap-2 pt-2">
              {[1,2,3,4,5,6,7,8,9,"⌫",0,"OK"].map((k) => (
                <button
                  key={k}
                  onClick={() => {
                    if (k === "⌫") setTableCode(prev => prev.slice(0, -1));
                    else if (k === "OK") handleCodeSearch();
                    else setTableCode(prev => prev + String(k));
                  }}
                  className={`h-12 rounded-xl font-bold text-lg transition active:scale-95 ${
                    k === "OK" ? "bg-emerald-600 text-white" :
                    k === "⌫" ? "bg-red-50 text-red-500" :
                    "bg-white border border-gray-200 text-gray-800"
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : tab === "cliente" ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-sm space-y-3">
            <p className="text-center text-xs text-gray-400 font-semibold uppercase tracking-wider mb-4">Selecionar tipo de pedido</p>

            {/* WhatsApp / Delivery */}
            <button
              className="w-full flex items-center gap-4 bg-green-50 border-2 border-green-200 rounded-xl p-4 hover:bg-green-100 active:scale-95 transition-all text-left"
              onClick={() => onClienteOrder?.("whatsapp")}
              data-testid="btn-tipo-whatsapp"
            >
              <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-black text-gray-800">WhatsApp / Delivery</p>
                <p className="text-xs text-gray-500 mt-0.5">Entrega em morada • notificação WhatsApp</p>
              </div>
            </button>

            {/* Balcão */}
            <button
              className="w-full flex items-center gap-4 bg-blue-50 border-2 border-blue-200 rounded-xl p-4 hover:bg-blue-100 active:scale-95 transition-all text-left"
              onClick={() => onClienteOrder?.("counter")}
              data-testid="btn-tipo-balcao"
            >
              <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-black text-gray-800">Balcão</p>
                <p className="text-xs text-gray-500 mt-0.5">Cliente no local • takeaway / levantamento</p>
              </div>
            </button>

            {/* Plataforma */}
            <button
              className="w-full flex items-center gap-4 bg-orange-50 border-2 border-orange-200 rounded-xl p-4 hover:bg-orange-100 active:scale-95 transition-all text-left"
              onClick={() => onClienteOrder?.("platform")}
              data-testid="btn-tipo-plataforma"
            >
              <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                <Monitor className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-black text-gray-800">Plataforma</p>
                <p className="text-xs text-gray-500 mt-0.5">Uber Eats, Glovo, etc. • pedido externo</p>
              </div>
            </button>

            {/* Mesa */}
            <button
              className="w-full flex items-center gap-4 bg-amber-50 border-2 border-amber-200 rounded-xl p-4 hover:bg-amber-100 active:scale-95 transition-all text-left"
              onClick={() => setTab("mesas")}
              data-testid="btn-tipo-mesa"
            >
              <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                <UtensilsCrossed className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-black text-gray-800">Mesa</p>
                <p className="text-xs text-gray-500 mt-0.5">Selecionar mesa → lançar pedido</p>
              </div>
            </button>
          </div>
        </div>
      ) : loading ? (
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
      ) : (
        <div className="flex-1 overflow-y-auto overscroll-contain p-3">
          <div className="grid grid-cols-4 gap-2">
            {filtered.map(table => {
              const isOccupied = table.status === "occupied" || table.has_open_order;
              return (
                <button
                  key={table.id}
                  onClick={() => onSelectTable({ ...table, isOccupied })}
                  className={`rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center min-h-[80px] sm:min-h-[90px] transition-all active:scale-95 ${
                    isOccupied
                      ? "bg-amber-500 text-white shadow-md"
                      : "bg-emerald-500 text-white shadow-sm hover:bg-emerald-600"
                  }`}
                  data-testid={`table-tile-${table.id}`}
                >
                  <span className="text-[11px] sm:text-xs font-black leading-tight text-center uppercase">
                    {table.name || `MESA ${String(table.number).padStart(2, '0')}`}
                  </span>
                  <span className="text-[9px] sm:text-[10px] mt-1 opacity-80">
                    {isOccupied ? `Aberta${table.total ? ` - €${table.total.toFixed(2)}` : ''}` : "Livre"}
                  </span>
                </button>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{tab === "abertas" ? "Nenhuma conta aberta" : "Nenhuma mesa cadastrada"}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
